// js/views/crm-poc.ts — Twenty CRM read-only cockpit (T9 → W5 multi-opportunity).
// Endpoints: GET /api/v2/crm/opportunities (liste) · GET /api/v2/crm/opportunity-summary?ref= (detay)
// SADECE OKUMA. Twenty'ye yazma YOK · müşteriye gönderim YOK · approval bypass YOK.
// W5: tek Corelsan kartı → liste (ad/stage/failed göstergesi) + tıkla→detay. Mevcut dashboard
// sınıfları (view-header, gb-card, gb-kpi-grid, gb-pill, muted); yeni CSS/asset YOK.
// Durum rengi yerine ✓/⏳/⚠ ikon (a11y — renk-tek-başına değil).

import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';

interface TwentyRec { [k: string]: unknown; id?: string; name?: string }
interface FollowupBrief {
  recommendedAction: string | null; eventCount: number; approvalRequired: boolean; sent: boolean;
  noteId: string | null; generatedAt: string | null;
}
interface ApprovalAction { type: string; detail: string; status: string }
interface Summary {
  opportunityRef: string; twentyConfigured: boolean;
  company: TwentyRec | null; opportunity: TwentyRec | null;
  diagnosticReport: TwentyRec | null; proposal: TwentyRec | null;
  portalEvents: TwentyRec[]; latestFollowupBrief: FollowupBrief | null;
  sync: { lastSyncedAt: string | null; failedEvents: number };
  approval: { requiredActions: ApprovalAction[]; sent: boolean };
  // crm-operating-system §1 — next-action + due (opsiyonel; yoksa '—', geciken ⚠ ile işaretlenir).
  nextAction?: string | null;
  nextActionDueAt?: string | null;
}
interface SummaryResponse { ok: true; summary: Summary }

interface OppListItem {
  ref: string; name: string; stage: string; lastSyncedAt: string | null; failedEvents: number;
  // crm-operating-system §1 — next-action + due (opsiyonel; backend taşımıyorsa undefined → '—').
  nextAction?: string | null;
  nextActionDueAt?: string | null;
}
interface OppListResponse { ok: true; twentyConfigured: boolean; opportunities: OppListItem[] }

type ListState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; twentyConfigured: boolean; items: OppListItem[] }
  | { kind: 'error'; message: string };

type DetailState =
  | { kind: 'none' }
  | { kind: 'loading'; ref: string }
  | { kind: 'ready'; summary: Summary }
  | { kind: 'error'; ref: string; message: string };

let listState: ListState = { kind: 'idle' };
let detailState: DetailState = { kind: 'none' };

function str(v: unknown, fallback = '—'): string {
  const s = typeof v === 'string' ? v : v == null ? '' : String(v);
  return s.length ? s : fallback;
}

/** due geçmiş mi? Geçersiz/boş tarih → false (uydurma YOK). */
function isOverdue(due: string | null | undefined): boolean {
  if (!due) return false;
  const t = Date.parse(due);
  return Number.isFinite(t) && t < Date.now();
}

// Next-action + due satırı (a11y: geciken ⚠ ikon + metin; renk-tek-başına DEĞİL).
// next-action yoksa "tanımsız" (crm-operating-system §1 — next-action yoksa iş YOK sayılır).
function nextActionLine(nextAction: string | null | undefined, due: string | null | undefined): string {
  if (!nextAction) {
    return `<span class="muted">Next-action: tanımsız (iş tanımlı değil)</span>`;
  }
  const overdue = isOverdue(due);
  const dueTxt = due ? str(due) : 'süre yok';
  const flag = overdue ? '⚠ GECİKTİ' : '⏳';
  return `<span class="muted">Next-action: ${flag} ${escHtml(nextAction)} <span class="muted">(due: ${escHtml(dueTxt)})</span></span>`;
}

export async function loadCrmPoc(): Promise<void> {
  listState = { kind: 'loading' };
  detailState = { kind: 'none' };
  const r = await apiGetJson<OppListResponse>('/api/v2/crm/opportunities');
  if (r.ok) listState = { kind: 'ready', twentyConfigured: r.data.twentyConfigured, items: r.data.opportunities };
  else listState = { kind: 'error', message: r.message ?? r.error };
}

async function loadDetail(ref: string): Promise<void> {
  detailState = { kind: 'loading', ref };
  renderCrmPoc();
  const r = await apiGetJson<SummaryResponse>('/api/v2/crm/opportunity-summary', { ref });
  if (r.ok) detailState = { kind: 'ready', summary: r.data.summary };
  else detailState = { kind: 'error', ref, message: r.message ?? r.error };
  renderCrmPoc();
}

export function renderCrmPoc(): void {
  const root = document.getElementById('view-crm-poc');
  if (!root) return;
  root.innerHTML = renderCrmPocHtml(listState, detailState);
  // Satır tıklaması → read-only detay yükle (her render'da yeniden bağlanır).
  root.querySelectorAll<HTMLButtonElement>('[data-opp-ref]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const ref = btn.getAttribute('data-opp-ref');
      if (ref) void loadDetail(ref);
    });
  });
}

function kpi(label: string, value: string): string {
  return `<div><div class="gb-kpi-label">${escHtml(label)}</div><div class="gb-kpi-value">${escHtml(value)}</div></div>`;
}
function section(label: string, body: string): string {
  return `<div style="margin-top:14px"><div class="gb-kpi-label">${escHtml(label)}</div>${body}</div>`;
}

// Canonical opportunity lifecycle stage sırası (twenty-crm-record-standard §5/§6 + canonical-lifecycle).
// Bilinmeyen/boş stage → 'other' grubuna düşer (uydurma YOK — gerçek stage neyse o gösterilir).
const STAGE_ORDER = ['new', 'qualified', 'proposed', 'won', 'lost', 'other'] as const;
type StageKey = (typeof STAGE_ORDER)[number];
const STAGE_LABEL: Record<StageKey, string> = {
  new: 'NEW (yeni)',
  qualified: 'Qualified (nitelikli)',
  proposed: 'Proposed (teklif)',
  won: 'Won (kazanıldı)',
  lost: 'Lost (kayıp)',
  other: 'Diğer / bilinmiyor',
};

/** Twenty stage değerini canonical gruba indirger; eşleşmezse 'other'. */
function stageKey(raw: string): StageKey {
  const v = raw.trim().toLowerCase();
  if (!v) return 'other';
  if (v.includes('new')) return 'new';
  if (v.includes('qualif')) return 'qualified';
  if (v.includes('propos')) return 'proposed';
  if (v.includes('won')) return 'won';
  if (v.includes('lost')) return 'lost';
  return 'other';
}

function oppRow(o: OppListItem): string {
  const failBadge = o.failedEvents > 0
    ? `<span class="gb-pill">⚠ ${o.failedEvents} sync hatası</span>`
    : `<span class="muted">✓</span>`;
  const overdueBadge = isOverdue(o.nextActionDueAt)
    ? `<span class="gb-pill">⚠ next-action gecikti</span>`
    : '';
  return `<li style="margin:6px 0">
    <button type="button" class="sb-item" data-opp-ref="${escHtml(o.ref)}" style="width:auto">
      <b>${escHtml(o.name)}</b>
      <span class="muted"> — ${escHtml(str(o.stage))}</span>
    </button>
    ${failBadge}
    ${overdueBadge}
    <span class="muted"> son senkron: ${escHtml(str(o.lastSyncedAt))}</span>
    <div style="margin-top:2px">${nextActionLine(o.nextAction, o.nextActionDueAt)}</div>
  </li>`;
}

function renderListHtml(s: ListState): string {
  if (s.kind === 'loading' || s.kind === 'idle') {
    return `<div class="gb-card"><div class="gb-empty-sm">Yükleniyor…</div></div>`;
  }
  if (s.kind === 'error') {
    return `<div class="gb-card"><div class="gb-empty-sm">Liste alınamadı: ${escHtml(s.message)}</div></div>`;
  }
  if (!s.items.length) {
    const note = s.twentyConfigured
      ? 'Henüz opportunity yok.'
      : 'Twenty yapılandırılmamış — opportunity listesi boş (ledger verisi detayda görünür).';
    return `<div class="gb-card"><div class="gb-empty-sm">${escHtml(note)}</div></div>`;
  }
  // Stage-gruplu pipeline görünümü (kart sözleşmesi #4) — canonical sıra; boş grup gizlenir.
  const byStage = new Map<StageKey, OppListItem[]>();
  for (const o of s.items) {
    const k = stageKey(o.stage);
    (byStage.get(k) ?? byStage.set(k, []).get(k)!).push(o);
  }
  const groups = STAGE_ORDER
    .filter((k) => (byStage.get(k)?.length ?? 0) > 0)
    .map((k) => {
      const items = byStage.get(k)!;
      const rows = items.map(oppRow).join('');
      return `<div style="margin-top:12px">
        <div class="gb-kpi-label">⏳ ${escHtml(STAGE_LABEL[k])} (${items.length})</div>
        <ul style="list-style:none;padding:0;margin:6px 0 0">${rows}</ul>
      </div>`;
    })
    .join('');
  return `<div class="gb-card">
    <div class="gb-kpi-label">Pipeline — stage'e göre ${s.items.length} opportunity (detay için seç)</div>
    ${groups}
  </div>`;
}

function renderDetailHtml(d: DetailState): string {
  if (d.kind === 'none') return '';
  if (d.kind === 'loading') {
    return `<div class="gb-card"><div class="gb-empty-sm">Detay yükleniyor: ${escHtml(d.ref)}…</div></div>`;
  }
  if (d.kind === 'error') {
    return `<div class="gb-card"><div class="gb-empty-sm">Detay alınamadı (${escHtml(d.ref)}): ${escHtml(d.message)}</div></div>`;
  }

  const m = d.summary;
  const companyName = str(m.company?.name ?? m.company?.['domainName']);
  const stage = str(m.opportunity?.['stage'] ?? m.opportunity?.['name']);
  const diagStatus = str(m.diagnosticReport?.['status']);
  const propStatus = str(m.proposal?.['status']);

  // Güvenlik göstergesi (renk yerine ✓/⚠ ikon): sent daima false → ✓.
  const sentBadge = m.approval.sent
    ? `<span class="gb-pill">⚠ Müşteri aksiyonu GÖNDERİLDİ</span>`
    : `<span class="gb-pill">✓ Hiçbir müşteri aksiyonu gönderilmedi (read-only)</span>`;

  const approvalBody = m.approval.requiredActions.length
    ? `<ul>${m.approval.requiredActions
        .map((a) => `<li>⏳ <b>${escHtml(a.type)}</b> — ${escHtml(a.detail)} <span class="muted">(${escHtml(a.status)})</span></li>`)
        .join('')}</ul>`
    : `<div class="muted">Onay bekleyen müşteri aksiyonu yok.</div>`;

  const timelineBody = m.portalEvents.length
    ? `<ul>${m.portalEvents
        .map((e) => `<li><b>${escHtml(str(e['eventType']))}</b> <span class="muted">${escHtml(str(e['occurredAt']))}</span></li>`)
        .join('')}</ul>`
    : `<div class="muted">Portal etkinliği yok.</div>`;

  const briefBody = m.latestFollowupBrief
    ? `<div>${escHtml(str(m.latestFollowupBrief.recommendedAction))}</div>
       <div class="muted">Etkinlik: ${m.latestFollowupBrief.eventCount} · ${
        m.latestFollowupBrief.approvalRequired ? 'onay gerekli' : 'onay gerekmez'
      } · gönderildi: ${m.latestFollowupBrief.sent ? 'evet' : 'hayır'}</div>`
    : `<div class="muted">Henüz follow-up brief yok.</div>`;

  const syncBody = `<div class="muted">Son senkron: ${escHtml(str(m.sync.lastSyncedAt))} · Bu opportunity'nin başarısız sync olayı: ${m.sync.failedEvents}</div>`;
  const twentyNote = m.twentyConfigured
    ? ''
    : `<div class="muted" style="margin-top:10px">(Twenty yapılandırılmamış — Twenty alanları boş; ledger verisi gösteriliyor.)</div>`;

  return `<div class="gb-card" style="margin-top:12px">
      <div style="margin-bottom:10px">${sentBadge}</div>
      <div class="gb-kpi-grid">
        ${kpi('Company', companyName)}
        ${kpi('Opportunity Stage', stage)}
        ${kpi('Diagnostic Report', diagStatus)}
        ${kpi('Proposal', propStatus)}
      </div>
      ${section('Next-action', `<div>${nextActionLine(m.nextAction, m.nextActionDueAt)}</div>`)}
      ${section('PortalEvent timeline', timelineBody)}
      ${section('Son follow-up brief', briefBody)}
      ${section('Approval-required customer actions', approvalBody)}
      ${section('Sync', syncBody)}
      ${section('opportunityRef', `<div class="muted">${escHtml(m.opportunityRef)}</div>`)}
      ${twentyNote}
    </div>`;
}

export function renderCrmPocHtml(list: ListState, detail: DetailState): string {
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">CRM Cockpit</div>
        <div class="view-sub">Twenty CRM — read-only liste + detay (W5). Yazma/gönderim YOK.</div>
      </div>
    </div>`;
  return header + renderListHtml(list) + renderDetailHtml(detail);
}
